import React from "react";
import { Checkbox } from "@/components/ui/checkbox";
import { CatalogMetadata } from "@/modules/learningCenter/features/CourseDirectory/api/getCatalogMetadata";
import { CourseDirectoryFilters } from "@/modules/learningCenter/features/CourseDirectory/hooks/useCourseDirectory";

interface CourseFilterSidebarProps {
  metadata: CatalogMetadata | null;
  filters: CourseDirectoryFilters;
  onToggle: <K extends keyof CourseDirectoryFilters>(
    facet: K,
    value: CourseDirectoryFilters[K][number],
  ) => void;
}

interface FacetSectionProps {
  title: string;
  children: React.ReactNode;
}

function FacetSection({ title, children }: FacetSectionProps) {
  return (
    <div className="py-3 border-b border-gray-200 dark:border-slate-700 last:border-b-0">
      <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-2">
        {title}
      </h3>
      <div className="flex flex-col gap-2">{children}</div>
    </div>
  );
}

interface CheckRowProps {
  label: string;
  checked: boolean;
  onChange: () => void;
  inputId: string;
}

function CheckRow({ label, checked, onChange, inputId }: CheckRowProps) {
  return (
    <label
      className="flex items-center gap-2.5 text-sm text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white cursor-pointer transition-colors"
      htmlFor={inputId}
    >
      <Checkbox
        checked={checked}
        id={inputId}
        onCheckedChange={() => onChange()}
      />
      <span className="capitalize">{label}</span>
    </label>
  );
}

// Filter facet body — the level / trades / job-roles sections, shared between
// the sticky desktop sidebar and the mobile filter Sheet.
export function CourseFilterSidebarBody({
  metadata,
  filters,
  onToggle,
}: CourseFilterSidebarProps) {
  return (
    <>
      {metadata && metadata.levels.length > 0 && (
        <FacetSection title="Level">
          {metadata.levels.map((level) => (
            <CheckRow
              checked={filters.levels.includes(level)}
              inputId={`level-${level}`}
              key={level}
              label={level}
              onChange={() => onToggle("levels", level)}
            />
          ))}
        </FacetSection>
      )}

      {metadata && metadata.trades.length > 0 && (
        <FacetSection title="Trades">
          {metadata.trades.map((trade) => (
            <CheckRow
              checked={filters.tradeId.includes(trade.id)}
              inputId={`trade-${trade.id}`}
              key={trade.id}
              label={trade.name}
              onChange={() => onToggle("tradeId", trade.id)}
            />
          ))}
        </FacetSection>
      )}
    </>
  );
}

export function CourseFilterSidebar(props: CourseFilterSidebarProps) {
  return (
    <div className="bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-700 rounded-lg px-4 py-1">
      <h2 className="pt-3 pb-2 text-sm font-medium text-gray-900 dark:text-white border-b border-gray-200 dark:border-slate-700">
        Filters
      </h2>
      <CourseFilterSidebarBody {...props} />
    </div>
  );
}
